import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Header } from "@/components/Header";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const attemptsRef = useRef(0);
  const { refresh } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refresh();
          setStatus("paid");
          return;
        }
        if (data.status === "expired" || attemptsRef.current >= 8) {
          setStatus("expired");
          return;
        }
      } catch {
        if (attemptsRef.current >= 8) {
          setStatus("error");
          return;
        }
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId, refresh]);

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="max-w-xl mx-auto px-4 py-16">
        <Card className="p-10 rounded-2xl bg-white border-slate-200 text-center">
          {status === "polling" && (
            <>
              <Loader2 className="h-12 w-12 mx-auto text-slate-400 animate-spin" />
              <h1 className="font-display font-bold text-2xl mt-4 text-slate-900">Confirming your payment…</h1>
              <p className="mt-2 text-slate-500 text-sm">This usually takes a few seconds.</p>
            </>
          )}
          {status === "paid" && (
            <>
              <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-600" />
              <h1 className="font-display font-bold text-3xl mt-4 text-slate-900" data-testid="payment-success-heading">You're Pro</h1>
              <p className="mt-2 text-slate-600">Your AI insights and full charts are unlocked.</p>
              <Button onClick={() => navigate("/dashboard")} className="mt-6 bg-slate-900 hover:bg-slate-800 h-11" data-testid="payment-success-cta">
                Go to dashboard <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </>
          )}
          {(status === "expired" || status === "error") && (
            <>
              <XCircle className="h-12 w-12 mx-auto text-red-500" />
              <h1 className="font-display font-bold text-2xl mt-4 text-slate-900">Payment not completed</h1>
              <p className="mt-2 text-slate-500 text-sm">Your session expired or was cancelled.</p>
              <Link to="/upgrade">
                <Button className="mt-6 bg-slate-900 hover:bg-slate-800 h-11">Try again</Button>
              </Link>
            </>
          )}
        </Card>
      </main>
    </div>
  );
}
